import React, { useState, useEffect, useMemo } from 'react';
import queryString from 'query-string';
import {
  Container,
  Gap,
  HeaderWrapper,
  MainWrapper,
} from 'lib/components/styles/layout';
import { Heading } from 'lib/components/styles/typography';
import {
  LoadingIndicator,
  ModalVideoListVideo,
  Table,
  TableContextProvider,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
} from 'lib/components';
import { FaFilter } from 'react-icons/fa';
import { useTheme } from 'styled-components/macro';
import { Activity, ActivityHeader, useAuth } from 'lib/context';
import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
  PaginationConstants,
} from 'lib/const/PaginationConstants';
import { TableHeader } from './styles';
import { useToastError, VideoData } from 'lib/hooks';
import { useSendActivity } from 'lib/api/activity/useSendActivity';
import {
  DateRangeSelector,
  SendReportModal,
  calculateDatesForRange,
} from '../../components';
import {
  getUserActivities,
  useGetUserActivitiesQuery,
} from 'lib/api/activity/getUserActivities';
import ActivityFilterModal from 'lib/components/modal/ActivityFilterModal';
import {
  ActivityFeedSortKey,
  ActivityTableColumns,
  baseFields,
  filterTableFields,
  formatColumns,
  prepareCsvData,
} from './ActivityTableUtils';
import Select from 'react-select';
import { selectStyle } from 'lib/style/commonStyles';
import { useGetUsersCustomerQuery } from 'lib/api/users/getUsersCustomers';
import { useLocation } from 'react-router';
import { useManagedDepartmentsQuery } from 'lib/api/departments/useManagedDepartmentsQuery';
import { Option } from 'lib/components/dropdown/DropdownOptions';
import { exportCSVFile } from 'lib/utils/functions';
import { Button } from 'react-covideo-common';
import dayjs from 'dayjs';
import { getDateRangeFromLocalstorage } from '../../components/DateRangeSelector/DateRangeSelector';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import { useCDK } from 'lib/hooks/useCdk';
import { timeRangeConstants } from '../../components/DateRangeSelector/useTimeRangeOptions';
import { useTranslation } from 'react-i18next';
import { Field } from 'lib/components/modal/ModalCustomTableFields/types';
import { ButtonWithTableFieldsModal } from 'lib/components/buttonWithTableFieldsModal';

// TO DO TEST WHEN DEPLOY COMMON - change all hover elements to use the new tooltip component
const ActivityFeed = () => {
  const { t, i18n } = useTranslation();
  const { showError } = useToastError();
  const themes = useTheme();
  const { userData } = useAuth();
  const { secondaryToText, secondaryToPrimary } = useCDK();
  const location = useLocation();

  const queryParams: any = queryString.parse(location.search);
  const storedDateRange = getDateRangeFromLocalstorage();

  const initRange =
    queryParams.range ||
    storedDateRange?.range ||
    timeRangeConstants.LAST_7_DAYS;
  const { start, end } = calculateDatesForRange(
    initRange,
    storedDateRange?.start,
    storedDateRange?.end
  );

  const paramsPage = queryParams.page;
  const paramsReceiveMethod = queryParams.receiveMethod;
  const paramsEmailStatus = queryParams.emailStatus;

  const { mutateAsync: sendEmail } = useSendActivity();

  const [page, setPage] = useState(Number(paramsPage) || DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [activeSort, setActiveSort] = useState(PaginationConstants.ASCENDING);
  const [sortKey, setSortKey] = useState(ActivityFeedSortKey.DATE_SENT);
  const [tableFields, setTableFields] = useState<Field[]>(baseFields(t));
  const [allTableFields, setAllTableFields] = useState<Field[]>(baseFields(t));
  const [startDate, setStartDate] = useState<Date>(start);
  const [endDate, setEndDate] = useState<Date>(end);
  const [dateRange, setDateRange] = useState(initRange);
  const [showVideoPreview, setShowVideoPreview] = useState<VideoData | null>(
    null
  );
  const [openRecipientsModal, setOpenRecipientsModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const filter = {
    receiveMethod: paramsReceiveMethod || '',
    emailStatus: paramsEmailStatus || '',
  };

  const { data: users, isLoading: areUsersLoading } = useGetUsersCustomerQuery({
    sort: PaginationConstants.ASCENDING,
  });

  const { data: departments, isLoading: areDepartmentsLoading } =
    useManagedDepartmentsQuery({
      customerId: userData.customerId,
    });

  const userSelectOptions = useMemo(
    () => [
      { label: t('All Users'), value: '' },
      ...(users || []).map(user => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      })),
    ],
    [users, t, i18n.language]
  );

  const departmentSelectOptions = (departments || []).map(department => ({
    label: department.name,
    value: department.departmentId,
  }));

  const [selectedUsers, setSelectedUsers] = useState<number[]>([
    Number(userSelectOptions[0].value),
  ]); // for the table
  const [selectedOption, setSelectedOption] = useState(userSelectOptions[0]); // for the dropdown

  useEffect(() => {
    if (selectedOption.value === '') {
      setSelectedUsers([Number(userSelectOptions[0].value)]);
      setSelectedOption(userSelectOptions[0]);
    }
    setTableFields(baseFields(t));
    setAllTableFields(baseFields(t));
  }, [i18n.language]);

  const fetchParams = {
    start: page * size,
    limit: size,
    order: activeSort,
    sortKey,
    from: startDate ? dayjs(startDate).format('MM/DD/YYYY') : '',
    to: endDate ? dayjs(endDate).format('MM/DD/YYYY') : '',
    senderIds: selectedUsers || [],
    ...filter,
  };

  const { data, isLoading } = useGetUserActivitiesQuery({
    ...fetchParams,
  });
  const { activities, count } = data || { activities: [], count: 0 };

  const filterCount =
    (!!filter.emailStatus ? 1 : 0) + (!!filter.receiveMethod ? 1 : 0);

  const onPaginationChange = ({
    page: newPage,
    size: newSize,
  }: {
    page: number;
    size: number;
  }) => {
    setSize(newSize);
    setPage(newSize !== size ? 0 : newPage);
  };

  const onDateRangeChange = (start: Date, end: Date, range: string) => {
    setStartDate(start);
    setEndDate(end);
    setDateRange(range);
  };

  const onSortingUpdate = (
    sortKey: ActivityFeedSortKey | undefined,
    order: PaginationConstants
  ) => {
    if (sortKey) {
      setSortKey(sortKey);
      setActiveSort(order);
      setPage(DEFAULT_PAGE);
    }
  };

  const isDepartment = (option: Option) =>
    departmentSelectOptions.find(
      departmentOption =>
        departmentOption.value === option.value &&
        departmentOption.label === option.label
    );

  const headers = tableFields.map(item => {
    return (
      <TableHeader>
        {item.label}
        {item?.sortable && item?.sortKey && (
          <SortButtons
            isSorted={sortKey === item.sortKey}
            order={activeSort}
            onChange={value =>
              onSortingUpdate(item.sortKey as ActivityFeedSortKey, value)
            }
          />
        )}
      </TableHeader>
    );
  });

  const rows =
    activities.map((activity: Activity, index: number) => ({
      key: index,
      columns: formatColumns(
        activity,
        tableFields,
        userData,
        setShowVideoPreview,
        allTableFields,
        t
      ),
    })) || [];

  const showNoResultsMessage =
    !count || (isDepartment(selectedOption) && !selectedUsers.length);

  const exportData = async () => {
    try {
      setIsExporting(true);
      const allActivity = await getUserActivities({
        ...fetchParams,
        limit: count,
      });
      const data = prepareCsvData(allActivity.activities, userData);
      const exportHeaders = getExportHeaders();
      exportCSVFile(exportHeaders, data, 'activity-report');
      setIsExporting(false);
    } catch (error) {
      setIsExporting(false);
      showError(
        error ||
          t('An error occurred exporting the data, please try again later.')
      );
    }
  };

  const sendActivityReport = async (recipients: string[]) => {
    const allActivity = await getUserActivities({
      ...fetchParams,
      limit: count,
    });

    const exportHeaders = getExportHeaders();
    const headers = Object.keys(exportHeaders).map(key => ({
      key,
      header: exportHeaders[key as keyof typeof exportHeaders] || '',
    }));
    const csvData = prepareCsvData(allActivity.activities, userData);
    await sendEmail({
      csvData,
      recipients,
      headers: headers,
    });
  };

  useEffect(() => {
    if (
      userData.isCompanyAdmin &&
      tableFields?.[1]?.value !== ActivityTableColumns.USER
    ) {
      const updatedFields = [...tableFields];
      updatedFields.splice(1, 0, {
        label: t('User'),
        value: ActivityTableColumns.USER,
        sortable: true,
        sortKey: ActivityFeedSortKey.USER_NAME,
      });
      setTableFields(updatedFields);
      setAllTableFields(updatedFields);
    }
  }, [tableFields]);

  const getExportHeaders = () => {
    let expHeaders = {} as ActivityHeader;
    filterTableFields(tableFields).forEach(field => {
      expHeaders[field.value as keyof typeof expHeaders] = field.label;
    });
    expHeaders.email = 'Email';
    expHeaders.phone = 'Phone';
    return expHeaders;
  };

  useEffect(() => {
    if (!selectedOption.value) {
      setSelectedUsers([]);
      return;
    }

    const department = isDepartment(selectedOption);
    if (department) {
      const usersInDepartment = (users || []).filter(
        userInDept =>
          userInDept.DeptId?.toString() === department.value.toString()
      );
      setSelectedUsers(
        usersInDepartment.map(userInDept => Number(userInDept.id))
      );
      return;
    }
    setSelectedUsers([Number(selectedOption.value)]);
  }, [selectedOption]);

  const areFiltersDisabled = isLoading || isExporting;

  const onSaveCustomTableFields = (fields: Field[]) => {
    setTableFields(fields);
  };

  const renderTableComponent = () => {
    if (isLoading)
      return <LoadingIndicator isLoading={true} height={'300px'} />;
    if (isExporting)
      return (
        <LoadingIndicator
          isLoading={true}
          height={'300px'}
          text={t('File is being exported. This might take a while.')}
        />
      );
    return (
      <TableContextProvider
        total={count}
        initSize={size}
        initPage={page}
        onChange={onPaginationChange}
      >
        {showNoResultsMessage ? (
          <Gap m='50px 0 0 0' justifyContent='center'>
            {t('There is no data to show')}
          </Gap>
        ) : (
          <>
            <Table relative headers={headers} compact rows={rows} />
            <TableFooter>
              <TablePaginationNew />
              <TablePaginationSizeNew text={t('Show items:')} />
            </TableFooter>
          </>
        )}
      </TableContextProvider>
    );
  };
  return (
    <Container>
      <MainWrapper resetPadding={false}>
        <HeaderWrapper>
          <Gap>
            <Heading>{t('Activity Feed')}</Heading>
            <DateRangeSelector
              onDateRangeChange={onDateRangeChange}
              initialValue={dateRange}
              disabled={areFiltersDisabled}
            />
            {userData.isCompanyAdmin && (
              <div style={{ width: '200px' }}>
                <Select
                  key={i18n.language}
                  placeholder={t('All Users')}
                  isDisabled={
                    areUsersLoading || areDepartmentsLoading || isExporting
                  }
                  styles={selectStyle(themes)}
                  options={[
                    {
                      label: t('Departments'),
                      options: departmentSelectOptions,
                    },
                    {
                      label: t('Users'),
                      options: userSelectOptions,
                    },
                  ]}
                  value={selectedOption}
                  onChange={option => {
                    if (option) {
                      setSelectedOption(option);
                    }
                  }}
                />
              </div>
            )}
          </Gap>
          <Button
            text={
              filterCount
                ? t('{{filterCount}} Active Filter', {
                    count: filterCount,
                    filterCount,
                  })
                : t('Filter')
            }
            variant={secondaryToPrimary}
            icon={<FaFilter size={14} />}
            onClick={() => setOpenFilterModal(true)}
            disabled={areFiltersDisabled}
          />
        </HeaderWrapper>

        <Gap gap='8px' justifyContent='right'>
          <Button
            onClick={exportData}
            text={t('Export')}
            variant={secondaryToText}
            disabled={areFiltersDisabled}
          />
          <Button
            onClick={() => setOpenRecipientsModal(true)}
            text={t('Send')}
            variant={secondaryToText}
            disabled={areFiltersDisabled}
          />

          <ButtonWithTableFieldsModal
            onUpdateClick={onSaveCustomTableFields}
            defaultReports={filterTableFields(tableFields)}
            allFields={filterTableFields(allTableFields)}
            storageKey='ActivityFeedTable'
            setReports={setTableFields}
            reports={tableFields}
          />
        </Gap>
        {renderTableComponent()}
      </MainWrapper>
      {!!showVideoPreview && !!showVideoPreview.id && (
        <ModalVideoListVideo
          videoId={showVideoPreview.id}
          source={showVideoPreview.videoSource}
          handleModalClose={() => setShowVideoPreview(null)}
        />
      )}

      {openRecipientsModal && (
        <SendReportModal
          sendDataFunction={(recipients: string[]) =>
            sendActivityReport(recipients)
          }
          startDate={new Date()}
          endDate={new Date()}
          range=''
          handleModalClose={() => setOpenRecipientsModal(false)}
        />
      )}
      {openFilterModal && (
        <ActivityFilterModal
          onClose={() => setOpenFilterModal(false)}
          filter={filter}
        />
      )}
    </Container>
  );
};

export default ActivityFeed;
